import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  IconCheckList,
  IconHistory,
  IconHome,
  IconImage,
  IconLink,
  IconMenu,
  IconSetting,
  IconShield,
  IconShoppingBag,
  IconUserGroup,
} from '@douyinfe/semi-icons';
import { Button, Dropdown, Layout, Nav, Space } from '../ui/semi';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AccountMenu } from '../components/AccountMenu';
import { useAuth } from '../auth/AuthContext';
import { useAuthorization } from '../auth/AuthorizationContext';
import { BrandMark, useTenantBranding } from '../branding/tenantBranding';

const { Header, Sider, Content } = Layout;

export function AppShell() {
  const auth = useAuth();
  const authorization = useAuthorization();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>(() => [navigationGroupForPath(location.pathname)]);
  const { branding } = useTenantBranding();

  useEffect(() => {
    setOpenKeys([navigationGroupForPath(location.pathname)]);
  }, [location.pathname]);

  if (!auth.token || !auth.user) {
    return <Navigate to="/login" replace />;
  }
  if (authorization.loading) {
    return <div className="full-page centered-page"><LoadingIndicator /></div>;
  }

  const dashboardItem = { itemKey: '/', icon: <IconHome />, text: '分析页', description: '经营概览、近期趋势与待办事项' };
  const reportsItem = { itemKey: '/reports', icon: <IconHome />, text: '数据页', description: '周期、月度与年度经营报表' };
  const canOpenReports = ['order.read', 'customer.user.read', 'fund.withdrawal.read'].some((permission) => authorization.has(permission));
  const iamItem = { itemKey: '/iam', icon: <IconShield />, text: '权限管理', description: '角色、权限与成员授权' };
  const customerUserItem = { itemKey: '/users', icon: <IconUserGroup />, text: '用户列表', description: '业务用户、类型与账户状态' };
  const workerUserItem = { itemKey: '/users/workers', icon: <IconUserGroup />, text: '打手列表', description: '已入驻打手与账户状态' };
  const customerLevelItem = { itemKey: '/users/levels', icon: <IconUserGroup />, text: '用户等级', description: '等级、成长值与资料权限' };
  const workerLevelItem = { itemKey: '/users/worker-levels', icon: <IconUserGroup />, text: '打手等级', description: '接单限制、资金规则与等级能力' };
  const workerStatisticsItem = { itemKey: '/users/worker-statistics', icon: <span className="nav-finance-icon">¥</span>, text: '打手数据统计', description: '打手余额、保证金与待处理提现汇总' };
  const workerLeaderboardItem = { itemKey: '/users/worker-leaderboard', icon: <span className="nav-finance-icon">★</span>, text: '打手排行榜', description: '接单流水、接单单量与综合评分 Top 30' };
  const workerNoticeItem = { itemKey: '/users/worker-notices', icon: <IconUserGroup />, text: '打手专属通知', description: '定向通知与强制阅读规则' };
  const productCategoryItem = { itemKey: '/products/categories', icon: <IconShoppingBag />, text: '商品分类', description: '维护两级商品分类与上下架状态' };
  const productItem = { itemKey: '/products', icon: <IconShoppingBag />, text: '商品管理', description: '维护商品价格、图集与下单说明' };
  const orderItem = { itemKey: '/orders', icon: <IconCheckList />, text: '商品全部订单', description: '查看服务订单及支付、接单状态' };
  const paidOrderItem = { itemKey: '/orders/paid', icon: <IconCheckList />, text: '商品付款订单', description: '查看已付款的商品订单及履约状态' };
  const unpaidOrderItem = { itemKey: '/orders/unpaid', icon: <IconCheckList />, text: '商品未付订单', description: '查看尚未付款的商品订单' };
  const refundPendingOrderItem = { itemKey: '/orders/refund-pending', icon: <IconCheckList />, text: '商品退款未处理', description: '查看尚未处理的退款申请' };
  const refundProcessedOrderItem = { itemKey: '/orders/refund-processed', icon: <IconCheckList />, text: '商品退款已处理', description: '查看已处理完成的退款订单' };
  const paidWaitingOrderItem = { itemKey: '/orders/paid-waiting', icon: <IconCheckList />, text: '商品已付款待接单', description: '查看已付款且尚未接单的订单' };
  const inProgressOrderItem = { itemKey: '/orders/in-progress', icon: <IconCheckList />, text: '商品已接单待完成', description: '查看已接单且服务尚未完成的订单' };
  const pendingSettlementOrderItem = { itemKey: '/orders/pending-settlement', icon: <IconCheckList />, text: '商品已完成待结单', description: '查看等待用户确认结单的订单' };
  const settledOrderItem = { itemKey: '/orders/settled', icon: <IconCheckList />, text: '商品已完成已结单', description: '查看已完成结单的订单' };
  const depositTopupOrderItem = { itemKey: '/orders/deposit-topups', icon: <IconCheckList />, text: '保证金充值订单', description: '查看打手保证金充值的支付与入账结果' };
  const orderItems = [orderItem, paidOrderItem, unpaidOrderItem, refundPendingOrderItem, refundProcessedOrderItem,
    paidWaitingOrderItem, inProgressOrderItem, pendingSettlementOrderItem, settledOrderItem,
    depositTopupOrderItem];
  const withdrawalItem = { itemKey: '/finance/withdrawals', icon: <span className="nav-finance-icon">¥</span>, text: '全部提现', description: '审核提现、查看收款资料与登记打款' };
  const homeBannerItem = { itemKey: '/storefront/home-banner', icon: <IconImage />, text: '首页 Banner', description: '管理移动端首页轮播图' };
  const announcementItem = { itemKey: '/announcements', icon: <IconSetting />, text: '平台公告', description: '发布和撤回全员公告' };
  const auditLogItem = { itemKey: '/audit-logs', icon: <IconHistory />, text: '操作日志', description: '查看当前租户业务操作与详情' };
  const loginLogItem = { itemKey: '/login-logs', icon: <IconHistory />, text: '登录日志', description: '查看租户登录审计' };
  const wechatItem = { itemKey: '/wechat', icon: <IconLink />, text: '微信配置', description: '公众号与普通商户直连配置' };
  const canOpenIam = authorization.has('iam.role.read') || authorization.has('iam.member.read');
  const canOpenCustomerUsers = authorization.has('customer.user.read');
  const canOpenCustomerLevels = authorization.has('customer.level.read');
  const canOpenWorkerLevels = authorization.has('worker.level.read');
  const canOpenApplications = authorization.has('worker.application.read');
  const canOpenWorkerStatistics = authorization.has('fund.withdrawal.read');
  const canOpenWorkerLeaderboard = authorization.has('order.read');
  const canOpenWorkerNotices = authorization.has('worker.notice.read');
  const applicationItem = { itemKey: '/users/worker-applications', icon: <IconUserGroup />, text: '打手申请', description: '入驻申请与审核' };
  const canOpenProductCategories = authorization.has('catalog.category.read');
  const canOpenProducts = authorization.has('catalog.product.read');
  const canOpenOrders = authorization.has('order.read');
  const fineItem = { itemKey: '/finance/fines', text: '用户罚款', description: '打手罚款与交款记录', icon: <span>¥</span> };
  const canOpenFines = authorization.has('fund.fine.read');
  const canOpenWithdrawals = authorization.has('fund.withdrawal.read');
  const canOpenHomeBanner = authorization.has('catalog.product.read');
  const canOpenAnnouncements = authorization.has('announcement.read');
  const canOpenLoginLogs = authorization.has('audit.log.read');
  const canOpenWechat = authorization.has('integration.wechat.read');
  const systemManagementItems = [
    ...(canOpenIam ? [{ itemKey: iamItem.itemKey, text: iamItem.text }] : []),
    ...(canOpenHomeBanner ? [{ itemKey: homeBannerItem.itemKey, text: homeBannerItem.text }] : []),
    ...(canOpenAnnouncements ? [{ itemKey: announcementItem.itemKey, text: announcementItem.text }] : []),
    ...(canOpenLoginLogs ? [{ itemKey: auditLogItem.itemKey, text: auditLogItem.text }] : []),
    ...(canOpenLoginLogs ? [{ itemKey: loginLogItem.itemKey, text: loginLogItem.text }] : []),
    ...(canOpenWechat ? [{ itemKey: wechatItem.itemKey, text: wechatItem.text }] : []),
  ];
  const quickItems = [
    dashboardItem,
    ...(canOpenReports ? [reportsItem] : []),
    ...(canOpenCustomerUsers ? [customerUserItem] : []),
    ...(canOpenCustomerLevels ? [customerLevelItem] : []),
    ...(canOpenCustomerUsers ? [workerUserItem] : []),
    ...(canOpenWorkerLevels ? [workerLevelItem] : []),
    ...(canOpenApplications ? [applicationItem] : []),
    ...(canOpenWorkerNotices ? [workerNoticeItem] : []),
    ...(canOpenWorkerStatistics ? [workerStatisticsItem] : []),
    ...(canOpenWorkerLeaderboard ? [workerLeaderboardItem] : []),
    ...(canOpenProductCategories ? [productCategoryItem] : []),
    ...(canOpenProducts ? [productItem] : []),
    ...(canOpenOrders ? orderItems : []),
    ...(canOpenWithdrawals ? [withdrawalItem] : []),
    ...(canOpenFines ? [fineItem] : []),
    ...(canOpenHomeBanner ? [homeBannerItem] : []),
    ...(canOpenAnnouncements ? [announcementItem] : []),
    ...(canOpenIam ? [iamItem] : []),
    ...(canOpenLoginLogs ? [auditLogItem] : []),
    ...(canOpenLoginLogs ? [loginLogItem] : []),
    ...(canOpenWechat ? [wechatItem] : []),
  ];
  const navigationItems = [
    { itemKey: 'dashboard-management', icon: <IconHome />, text: '仪表盘', items: [
      { itemKey: dashboardItem.itemKey, text: dashboardItem.text },
      ...(canOpenReports ? [{ itemKey: reportsItem.itemKey, text: reportsItem.text }] : []),
    ] },
    ...(canOpenCustomerUsers || canOpenCustomerLevels || canOpenWorkerLevels || canOpenApplications || canOpenWorkerNotices || canOpenWorkerStatistics || canOpenWorkerLeaderboard ? [{
      itemKey: 'user-management',
      icon: <IconUserGroup />,
      text: '用户管理',
      items: [
        ...(canOpenCustomerUsers ? [{ itemKey: customerUserItem.itemKey, text: customerUserItem.text }] : []),
        ...(canOpenCustomerLevels ? [{ itemKey: customerLevelItem.itemKey, text: customerLevelItem.text }] : []),
        ...(canOpenCustomerUsers ? [{ itemKey: workerUserItem.itemKey, text: workerUserItem.text }] : []),
        ...(canOpenWorkerLevels ? [{ itemKey: workerLevelItem.itemKey, text: workerLevelItem.text }] : []),
        ...(canOpenApplications ? [{ itemKey: applicationItem.itemKey, text: applicationItem.text }] : []),
        ...(canOpenWorkerNotices ? [{ itemKey: workerNoticeItem.itemKey, text: workerNoticeItem.text }] : []),
        ...(canOpenWorkerStatistics ? [{ itemKey: workerStatisticsItem.itemKey, text: workerStatisticsItem.text }] : []),
        ...(canOpenWorkerLeaderboard ? [{ itemKey: workerLeaderboardItem.itemKey, text: workerLeaderboardItem.text }] : []),
      ],
    }] : []),
    ...(canOpenProductCategories || canOpenProducts ? [{
      itemKey: 'catalog-management',
      icon: <IconShoppingBag />,
      text: '商品中心',
      items: [
        ...(canOpenProductCategories ? [{ itemKey: productCategoryItem.itemKey, text: productCategoryItem.text }] : []),
        ...(canOpenProducts ? [{ itemKey: productItem.itemKey, text: productItem.text }] : []),
      ],
    }] : []),
    ...(canOpenOrders ? [{ itemKey: 'order-management', icon: <IconCheckList />, text: '订单中心',
      items: orderItems.map((item) => ({ itemKey: item.itemKey, text: item.text })) }] : []),
    ...((canOpenWithdrawals || canOpenFines) ? [{ itemKey: 'finance-management', icon: <span className="nav-finance-icon">¥</span>, text: '财务管理', items: [...(canOpenWithdrawals ? [{ itemKey: withdrawalItem.itemKey, text: withdrawalItem.text }] : []), ...(canOpenFines ? [{ itemKey: fineItem.itemKey, text: fineItem.text }] : [])] }] : []),
    {
      itemKey: 'system-management',
      icon: <IconSetting />,
      text: '系统管理',
      items: systemManagementItems,
    },
  ];
  const selected = location.pathname === '/reports'
    ? '/reports'
    : location.pathname.startsWith('/iam')
    ? '/iam'
    : location.pathname.startsWith('/storefront/home-banner')
      ? '/storefront/home-banner'
    : location.pathname.startsWith('/announcements')
      ? '/announcements'
    : location.pathname.startsWith('/products/categories')
      ? '/products/categories'
    : location.pathname === '/products'
      ? '/products'
    : orderItems.some((item) => item.itemKey === location.pathname)
      ? location.pathname
    : location.pathname.startsWith('/finance/fines')
      ? '/finance/fines'
    : location.pathname.startsWith('/finance/withdrawals')
      ? '/finance/withdrawals'
    : location.pathname.startsWith('/wechat')
      ? '/wechat'
    : location.pathname.startsWith('/users/levels')
      ? '/users/levels'
      : location.pathname.startsWith('/users/worker-leaderboard')
        ? '/users/worker-leaderboard'
      : location.pathname.startsWith('/users/worker-statistics')
        ? '/users/worker-statistics'
      : location.pathname.startsWith('/users/worker-levels')
        ? '/users/worker-levels'
      : location.pathname.startsWith('/users/worker-notices')
        ? '/users/worker-notices'
      : location.pathname === '/users/workers'
        ? '/users/workers'
      : location.pathname === '/users/worker-applications'
        ? '/users/worker-applications'
      : location.pathname === '/users'
        ? '/users'
      : location.pathname.startsWith('/audit-logs')
        ? '/audit-logs'
      : location.pathname.startsWith('/login-logs')
        ? '/login-logs'
        : location.pathname === '/'
          ? '/'
          : null;
  const siderWidth = collapsed ? 72 : 248;

  return (
    <Layout className={collapsed ? 'app-shell app-shell-collapsed' : 'app-shell'}>
      <Sider style={{ width: siderWidth, flex: `0 0 ${siderWidth}px` }} className={collapsed ? 'app-sider is-collapsed' : 'app-sider'}>
        <Nav
          className="app-navigation"
          style={{ height: '100%' }}
          selectedKeys={selected ? [selected] : []}
          items={navigationItems}
          isCollapsed={collapsed}
          openKeys={openKeys}
          onOpenChange={({ itemKey, isOpen }) => setOpenKeys(isOpen ? [String(itemKey)] : [])}
          onCollapseChange={setCollapsed}
          header={{
            logo: <BrandMark className="app-logo-mark" displayName={branding.displayName} logoUrl={branding.logoUrl} />,
            text: <span className="app-logo-copy"><strong>{branding.displayName}</strong></span>,
          }}
          footer={{ collapseButton: true, collapseText: (isCollapsed) => isCollapsed ? '展开导航' : '收起导航' }}
          onSelect={({ itemKey }) => {
            const target = String(itemKey);
            if (target.startsWith('/')) navigate(target);
          }}
        />
      </Sider>
      <Layout>
        <Header className="app-header">
          <div className="header-context">
            <span className="mobile-nav">
              <Dropdown
                trigger="click"
                menu={quickItems.filter((item) => !('disabled' in item && item.disabled)).map((item) => ({ node: 'item' as const, name: String(item.text), onClick: () => navigate(String(item.itemKey)) }))}
              >
                <Button theme="borderless" icon={<IconMenu />} aria-label="打开租户导航" />
              </Dropdown>
            </span>
          </div>
          <Space className="header-tools" spacing={12}>
            <AccountMenu
              name={auth.user.displayName}
              caption={auth.user.username}
              initials={auth.user.displayName.slice(0, 1).toUpperCase()}
              onOpenProfile={() => navigate('/profile')}
              onLogout={auth.logout}
            />
          </Space>
        </Header>
        <Content className="app-content"><Outlet /></Content>
      </Layout>
    </Layout>
  );
}

function navigationGroupForPath(pathname: string) {
  if (pathname === '/' || pathname.startsWith('/reports')) return 'dashboard-management';
  if (pathname.startsWith('/products')) return 'catalog-management';
  if (pathname.startsWith('/orders')) return 'order-management';
  if (pathname.startsWith('/finance')) return 'finance-management';
  if (pathname.startsWith('/iam') || pathname.startsWith('/storefront') || pathname.startsWith('/announcements') || pathname.startsWith('/audit-logs')
    || pathname.startsWith('/login-logs') || pathname.startsWith('/wechat')) {
    return 'system-management';
  }
  return 'user-management';
}
